// replay-events.ts — key-moment detection for the replay viewer's timeline markers.
//
// A collector rides along a from-seed re-sim (the checkpoint prewarm pass — worker OR the
// in-thread fallback, both of which always cover update 0 → end) and records the game's key
// events with the update they happened at:
//
//   • contact  — the FIRST CONTACT between each pair of sides, straight from the sim's own
//     sight sweep: UnitGob::NotifyEnemyNearby's DISCOVERY block updates each Player's
//     DiscoveredSides mask (SDIS — it is lockstep-hashed state, so it is exactly what the
//     game itself considers "seen"). The collector diffs the masks once per update.
//   • battle   — the FIRST BATTLE between each pair of sides: the first damage-dealing shot
//     launched between them (the NotifyShotFired host seam — every kmidHit originates at
//     TankShotGob::Launch / RocketGob::InitRocket, so this covers bullets, shells, rockets
//     and artillery alike).
//   • takeover — EVERY hostile takeover (the NotifyStructTakeover host seam at
//     StructGob::Takeover — the takeover-specialist capture), with the structure type.
//   • end      — mission end, from the existing EndMission/NotifyEndMission trigger seams.
//
// Host seams are fire-and-forget and nothing the sim branches on, so collecting events
// never perturbs the deterministic re-sim. Events are keyed (key()) so the main thread can
// merge batches idempotently — a superseded prewarm pass (fog-side change) re-detects the
// same events with the same keys and values.

import type { Sim } from '../../../sim/src/index.ts';
import { TcFromWc } from '../../../sim/src/math/coords.ts';

export type ReplayEventKind = 'contact' | 'battle' | 'takeover' | 'end';

export interface ReplayEvent {
  kind: ReplayEventKind;
  /** The sim update the event happened at (the marker's timeline position). */
  update: number;
  /** The primary side (contact/battle: the lower side of the pair; takeover: the CAPTURING
   *  side; end: the side the mission ended for). */
  a: number;
  /** contact/battle: the other side of the pair; takeover: the side that LOST the structure. */
  b?: number;
  /** Where it happened (tile coords) — contact: the discover point; battle: the shot;
   *  takeover: the structure center. */
  tx?: number;
  ty?: number;
  /** takeover: the captured structure's unit type (kut*) for a readable label. */
  ut?: number;
  /** end: whether side `a` won. */
  win?: boolean;
}

/** A per-side economy snapshot at one update — the expandable timeline's over-time graph is
 *  built from a stride of these. Deterministic sim state (credits + unit counts are hashed),
 *  so a superseded pass re-samples identical values → the main thread merges by `update`. */
export interface EconomySample {
  update: number;
  /** side → credits at `update` (only roster sides; neutral side 0 has no economy). */
  credits: Record<number, number>;
  /** side → total unit count at `update` (Player.m_acut summed — every gob the side owns). */
  units: Record<number, number>;
}

/** The default gap (in sim updates) between economy samples — ~2s of game time at 1×.
 *  A long all-bot game (~30k updates) still stays a few thousand points: cheap to post +
 *  draw, dense enough for a smooth line. */
export const kEconSampleStride = 25;

/** The stable merge key: one first-contact/first-battle per unordered side pair, every
 *  takeover (update+place disambiguates), one end per side+outcome. */
export function eventKey(ev: ReplayEvent): string {
  switch (ev.kind) {
    case 'contact':
    case 'battle':
      return `${ev.kind}:${ev.a}:${ev.b}`;
    case 'takeover':
      return `takeover:${ev.update}:${ev.tx}:${ev.ty}`;
    case 'end':
      return `end:${ev.a}:${ev.win === true ? 1 : 0}`;
  }
}

export interface EventCollector {
  /** Host-seam overrides — spread into the re-sim's host BEFORE Sim.create. */
  host: {
    NotifyShotFired(sideAssailant: number, sideTarget: number, wx: number, wy: number): void;
    NotifyStructTakeover(
      sideOld: number,
      sideNew: number,
      ut: number,
      wx: number,
      wy: number,
    ): void;
    EndMission(fWin: boolean, side: number): void;
    NotifyEndMission(side: number, fWin: boolean): void;
  };
  /** Bind the sim the events are read from (update counts + the discovery masks). */
  attach(sim: Sim): void;
  /** Call once per completed sim update — diffs the per-player discovery masks (contact) and
   *  samples the per-side economy on the stride. */
  pollUpdate(): void;
  /** Drain the events collected since the last drain (post/merge them incrementally). */
  drain(): ReplayEvent[];
  /** Drain the economy samples collected since the last drain (post/merge them by update). */
  drainSamples(): EconomySample[];
}

/** @param sides the roster's distinct sides — contact/battle pairs are confined to them
 *  (side 0 is neutral map property; a turret potting a neutral husk is not a battle).
 *  @param econStride updates between economy samples (default kEconSampleStride). */
export function makeEventCollector(
  sides: number[],
  econStride: number = kEconSampleStride,
): EventCollector {
  let sim: Sim | null = null;
  const out: ReplayEvent[] = [];
  const samples: EconomySample[] = []; // economy stride, drained alongside events
  const seen = new Set<string>(); // contact/battle pairs + ends already recorded
  const rosterSides = sides.filter((s) => s > 0);
  const lastMask = new Map<number, number>(); // per-side DiscoveredSides at the last poll
  let lastEconSample = -Infinity; // the update the economy was last sampled at

  const push = (ev: ReplayEvent): void => {
    const k = eventKey(ev);
    if (ev.kind !== 'takeover') {
      if (seen.has(k)) return;
      seen.add(k);
    }
    out.push(ev);
  };
  const update = (): number => (sim === null ? 0 : sim.sim.GetUpdateCount());
  const pairEvent = (
    kind: 'contact' | 'battle',
    sideA: number,
    sideB: number,
    tx?: number,
    ty?: number,
  ): void => {
    if (sideA === sideB) return;
    if (!rosterSides.includes(sideA) || !rosterSides.includes(sideB)) return;
    const [a, b] = sideA < sideB ? [sideA, sideB] : [sideB, sideA];
    push({ kind, update: update(), a, b, tx, ty });
  };

  interface PlayerView {
    GetDiscoveredSides(): number;
    GetDiscoverPoint(): { tx: number; ty: number };
    GetCredits(): number;
    /** count per unit type (Player.m_acut); its sum is the side's total unit count. */
    m_acut: number[];
  }
  const playersOf = (s: Sim): ((side: number) => PlayerView | null) => {
    const plrm = s.ctx.plrm as unknown as { GetPlayer(side: number): PlayerView | null };
    return (side) => plrm.GetPlayer(side);
  };
  // snapshot every roster side's credits + unit count at the current update (the graph's Y
  // series). The pre-first-step sim reads update -1 (the "before update 0" sentinel) — clamp
  // the baseline to 0 so the series starts cleanly at the timeline's origin.
  const sampleEconomy = (): void => {
    if (sim === null) return;
    const player = playersOf(sim);
    const credits: Record<number, number> = {};
    const units: Record<number, number> = {};
    for (const side of rosterSides) {
      const p = player(side);
      if (p !== null) {
        credits[side] = p.GetCredits();
        units[side] = p.m_acut.reduce((a, b) => a + b, 0); // every gob the side owns
      }
    }
    const u = Math.max(0, update());
    samples.push({ update: u, credits, units });
    lastEconSample = u;
  };

  return {
    host: {
      NotifyShotFired: (sideAssailant, sideTarget, wx, wy) =>
        pairEvent('battle', sideAssailant, sideTarget, TcFromWc(wx), TcFromWc(wy)),
      NotifyStructTakeover: (sideOld, sideNew, ut, wx, wy) => {
        if (sideOld === sideNew) return;
        push({
          kind: 'takeover',
          update: update(),
          a: sideNew,
          b: sideOld,
          ut,
          tx: TcFromWc(wx),
          ty: TcFromWc(wy),
        });
      },
      EndMission: (fWin, side) => push({ kind: 'end', update: update(), a: side, win: fWin }),
      NotifyEndMission: (side, fWin) => push({ kind: 'end', update: update(), a: side, win: fWin }),
    },
    attach(s: Sim): void {
      sim = s;
      lastMask.clear();
      lastEconSample = -Infinity;
      // baseline BEFORE any stepping: a side's own bit (and any pre-seeded ally bits) are
      // not contact events, but anything gained from update 1 on is.
      const player = playersOf(s);
      for (const side of rosterSides) {
        const p = player(side);
        if (p !== null) lastMask.set(side, p.GetDiscoveredSides());
      }
      sampleEconomy(); // the starting-credits point at update 0
    },
    pollUpdate(): void {
      if (sim === null) return;
      const player = playersOf(sim);
      for (const side of rosterSides) {
        const p = player(side);
        if (p === null) continue;
        const mask = p.GetDiscoveredSides();
        const prev = lastMask.get(side) ?? 0;
        lastMask.set(side, mask);
        let gained = (mask & ~prev) >>> 0;
        while (gained !== 0) {
          const bit = gained & -gained;
          gained = (gained & ~bit) >>> 0;
          const other = 31 - Math.clz32(bit); // GetSideMask(side) = 1 << side
          const tpt = p.GetDiscoverPoint();
          pairEvent('contact', side, other, tpt.tx, tpt.ty);
        }
      }
      if (update() - lastEconSample >= econStride) sampleEconomy();
    },
    drain(): ReplayEvent[] {
      return out.splice(0, out.length);
    },
    drainSamples(): EconomySample[] {
      return samples.splice(0, samples.length);
    },
  };
}
